// Phase MHM-5.2 · Admin top-bar bell badge for unread admin notifications.
// Polls `/api/admin/notifications/unread` every 30s. Click → panel of 10
// latest unread; clicking a row navigates to /dashboard/admin/approvals
// AND marks the row read.
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Loader2 } from "lucide-react";
import api from "../../services/api";

export default function AdminBellBadge() {
  const [count, setCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const nav = useNavigate();

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/notifications/unread?limit=10");
      setCount(data.count || 0); setRows(data.rows || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    poll();
    timerRef.current = setInterval(poll, 30_000);
    return () => clearInterval(timerRef.current);
  }, [poll]);

  const openRow = async (n) => {
    setLoading(true);
    try {
      await api.post("/admin/notifications/mark-read", { notification_ids: [n.id] });
      await poll();
      setOpen(false);
      nav("/dashboard/admin/approvals");
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const markAll = async () => {
    setLoading(true);
    try {
      await api.post("/admin/notifications/mark-read", { all: true });
      await poll();
    } finally { setLoading(false); }
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)}
              data-testid="admin-bell-badge"
              data-unread-count={count}
              className="relative p-2 rounded-full hover:bg-slate-100">
        <Bell className="h-5 w-5 text-slate-600"/>
        {count > 0 && (
          <span data-testid="admin-bell-badge-dot"
                className="absolute top-1 right-1 inline-block h-2.5 w-2.5 rounded-full bg-rose-500
                           ring-2 ring-white animate-pulse"/>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-slate-200 rounded-xl shadow-lg z-50"
             data-testid="admin-bell-panel">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
            <span className="text-xs font-semibold text-slate-600">Unread · {count}</span>
            <button onClick={markAll} disabled={count === 0 || loading}
                    className="text-xs text-slate-500 hover:text-slate-700 disabled:opacity-40"
                    data-testid="admin-bell-mark-all">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : "Mark all read"}
            </button>
          </div>
          <ul className="max-h-72 overflow-y-auto">
            {rows.length === 0 && (
              <li className="py-6 text-center text-xs text-slate-400">No unread notifications</li>
            )}
            {rows.map((r) => (
              <li key={r.id}>
                <button onClick={() => openRow(r)}
                        data-testid={`admin-bell-row-${r.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-orange-50 border-b border-slate-100">
                  <div className="text-xs font-mono text-slate-500">{r.event}</div>
                  <div className="text-sm text-slate-700">
                    {r.payload?.name ? `"${r.payload.name}"` : ""}
                    {r.payload?.requested_by && <> by <b>{r.payload.requested_by}</b></>}
                  </div>
                  <div className="text-xs text-slate-400">{r.sent_at?.slice(0, 16).replace("T", " ")}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
